import { html, css, LitElement } from "lit";
import { customElement, state, property } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

export interface NextPageEvent { }
export interface PrevPageEvent { }

@customElement("page-controls")
export class PageControls extends LitElement {
	static styles = css`
		* {
			box-sizing: border-box;
		}

		:host {
			display: contents;
		}

		.page-controls {
			position: static;
			min-height: 50px;
			background-color: white;
			width: 85vw;
			max-width: 700px;
			margin: 8px auto;
			border: 1px solid hsl(263deg 45% 56%);
			border-radius: 8px;
			display: flex;
			align-items: center;
			padding: 8px;
			gap: 8px;
			user-select: none;
		}

		.page-controls.pinned {
			position: sticky;
			bottom: 8px;
			z-index: 2;
		}

		.info {
			flex: 1;
		}

		.controls {
			display: flex;
			gap: 16px;
		}

		.fa-arrow-left,
		.fa-arrow-right,
		.fa-chevron-up,
		.fa-chevron-down {
			width: 32px;
			height: 32px;
			display: flex;
			justify-content: center;
			align-items: center;
			border-radius: 50%;
			color: white;
			background-color: hsl(263deg 45% 56%);
			cursor: pointer;
			transition: all 250ms;
			border: none;
		}

		.fa-chevron-up,
		.fa-chevron-down {
			background-color: white;
			color: hsl(263deg 45% 56%);
		}

		.page-controls.pinned .fa-chevron-up,
		.page-controls.pinned .fa-chevron-down {
			background-color: hsl(263deg 45% 56%);
			color: white;
		}

		.fa-chevron-up:hover,
		.fa-chevron-down:hover,
		.page-controls.pinned .fa-chevron-up:hover,
		.page-controls.pinned .fa-chevron-down:hover {
			background-color: hsl(263deg 45% 64%);
			color: white;
		}

		.fa-arrow-left,
		.fa-arrow-right {
			filter: grayscale();
		}

		.fa-arrow-left.enabled,
		.fa-arrow-right.enabled {
			filter: none;
		}

		.fa-arrow-left:not(.enabled):hover,
		.fa-arrow-right:not(.enabled):hover {
			cursor: default;
		}

		.fa-arrow-left.enabled:hover,
		.fa-arrow-right.enabled:hover {
			background-color: hsl(263deg 45% 64%);
		}
	`;

	@property({ type: Number, attribute: "current-page" })
	public currentPage = 0; /* zero-based index */

	@property({ type: Number, attribute: "total-pages" })
	public totalPages = 1;

	@state()
	private pinned = true;

	private handlePinClick() {
		this.pinned = !this.pinned;
	}

	private handlePrevPageClick() {
		const event = new CustomEvent<PrevPageEvent>("prevPage", {
			composed: true,
		});
		this.dispatchEvent(event);
	}

	private handleNextPageClick() {
		const event = new CustomEvent<NextPageEvent>("nextPage", {
			composed: true,
		});
		this.dispatchEvent(event);
	}

	render() {
		return html`
			<!-- Font Awesome -->
			<link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.3/css/all.min.css" integrity="sha512-iBBXm8fW90+nuLcSKlbmrPcLa0OT92xO1BIsZ+ywDWZCvqsWgccV3gFoRBv0z+8dLJgyAHIhR35VZc2oM/gI1w==" crossorigin="anonymous" referrerpolicy="no-referrer" />

			<div class="page-controls ${classMap({ pinned: this.pinned })}">
				<div class="pin">
					<button
						class="fas fa-fw ${classMap({
							"fa-chevron-up": !this.pinned,
							"fa-chevron-down": this.pinned,
						})}"
						aria-label = ${this.pinned ? "Move Page Controls to Bottom of Page" : "Pin Page Controls to Page"}
						@click=${this.handlePinClick}
					></button>
				</div>
				<div class="info">
					Page ${this.currentPage + 1} of ${this.totalPages}
				</div>
				<div class="controls">
					<div class="left-control">
						<button
							class="fas fa-arrow-left fa-fw ${classMap({
								enabled: this.currentPage > 0,
							})}"
							aria-label = "Previous Page"
							@click=${this.handlePrevPageClick}
						></button>
					</div>
					<div class="right-control">
						<button
							class="fas fa-arrow-right fa-fw ${classMap({
								enabled: this.currentPage + 1 < this.totalPages,
							})}"
							aria-label = "Next Page"
							@click=${this.handleNextPageClick}
						></button>
					</div>
				</div>
			</div>
		`;
	}
}